import React, { useState } from "react";
import * as S from "./InsertDataPage.style";
import { Modal } from "@/components/molecules/Modal";
import { InputText } from "@/components/atoms/Inputs/InputText/indext";
import * as xlsx from "xlsx";
import { useMutation } from "@tanstack/react-query";
import { request } from "@/lib/api";

export const InsertDataPage = () => {
  // 카테고리 (한글, 영어, 수학) 상태
  const [subjectType, setSubjectType] = useState<string>("korean");
  // 카테고리의 step 상태 (1~4)
  const [stepType, setStepType] = useState<number>(1);
  // 카테고리의 level 상태 (1~4)
  const [levelType, setLevelType] = useState<number>(1);
  // 카테고리의 세부 데이터(유닛, 유닛 디테일, 유닛 파일) 상태
  const [subDetailsType, setSubDetailsType] = useState<string>("units");
  // 모달 오픈 상태
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  // 로딩 상태
  const [isLoading, setIsLoading] = useState<boolean>(false);

  /** 데이터를 넣기 위한 모달 오픈 함수 */
  const OpenUploadsModal = (type: string) => {
    setIsModalOpen(true);
    setSubDetailsType(type);
  };
  interface FileUploadProps {
    project: string;
  }

  /** 파일 업로드 */
  const FileUpload = ({ project }: FileUploadProps) => {
    const [file, setFile] = useState<any>(null);

    const handleFileChange = (e: any) => {
      setFile(e.target.files[0]);
    };

    const { mutateAsync: filesUploads } = useMutation({
      mutationFn: (data: FormData) =>
        request({
          method: "POST",
          url:
            subjectType === "math"
              ? "/math-content/insert-data"
              : subjectType === "korean"
              ? "/korean-content/insert-data"
              : "/english-content/insert-data",
          data: data,
          // headers: {
          //   "Content-Type": "multipart/form-data",
          // },
          headers: { "Content-Type": "application/json" },
        }),
      onSuccess: () => {
        setIsLoading(false);
        alert("데이터가 등록되었습니다.");
        // setIsModalOpen(false);
      },
      onError: () => {
        setIsLoading(false);
      },
    });

    const handleUpload = async (data: any) => {
      try {
        setIsLoading(true);

        if (file) {
          const reader = new FileReader();
          reader.onload = async (e: ProgressEvent<FileReader>) => {
            if (e.target?.result) {
              const data = new Uint8Array(e.target.result as ArrayBuffer);
              const workbook = xlsx.read(data, { type: "array" });
              const sheetName = workbook.SheetNames[0];
              const sheet = workbook.Sheets[sheetName];
              const jsonData = xlsx.utils.sheet_to_json(sheet, {
                header: 1,
              }) as any[][];

              const header = jsonData[0];

              // ✅ 과목별 컬럼 매핑 정의
              const parseRules: Record<
                string, // subjectType
                Record<
                  string, // subDetailsType
                  (row: any[]) => Record<string, any>
                >
              > = {
                korean: {
                  units: (row) => ({
                    step_number: stepType,
                    level_number: levelType,
                    unit_number: row[0],
                    unit_title: row[1],
                    unit_point: row[2],
                    unit_main_img: row[3],
                  }),
                  details: (row) => {
                    const hasUnitVowel = header[2] === "unit_vowel";

                    const base = 2;

                    return {
                      step_number: stepType,
                      level_number: levelType,
                      unit_number: row[0],
                      unit_tab_name: row[1],
                      ...(hasUnitVowel
                        ? { unit_vowel: row[base] }
                        : { unit_sentence: row[2] }),
                      ...(hasUnitVowel ? { unit_speak: row[base + 1] } : {}),
                      unit_problem: hasUnitVowel
                        ? row[base + 2]
                        : row[base + 1],
                      unit_correct_word: hasUnitVowel
                        ? row[base + 3]
                        : row[base + 2],
                      unit_incorrect_word: hasUnitVowel
                        ? row[base + 4]
                        : row[base + 3],
                      unit_sort: hasUnitVowel ? row[base + 5] : row[base + 4],
                    };
                  },
                  files: (row) => {
                    const hasUnitSort =
                      row.length > 8 || typeof row[2] === "number";
                    const base = hasUnitSort ? 3 : 2;
                    return {
                      step_number: stepType,
                      level_number: levelType,
                      unit_number: row[0],
                      unit_tab_name: row[1],
                      ...(hasUnitSort ? { unit_sort: row[2] } : {}),
                      path: row[base],
                      type: row[base + 1],
                      sort: row[base + 2],
                      originalname: row[base + 3],
                      mimetype: row[base + 4],
                      size: row[base + 5],
                    };
                  },
                },
                english: {
                  units: (row) => ({
                    step_number: stepType,
                    level_number: levelType,
                    unit_number: row[0],
                    unit_title: row[1],
                    unit_point: row[2],
                    unit_main_img: row[3],
                  }),
                  details: (row) => {
                    const hasUnitVowel = header[2] === "unit_vowel";
                    const hasUnitKorean = header[3] === "unit_korean";

                    const base = hasUnitKorean ? 3 : 2;

                    return {
                      step_number: stepType,
                      level_number: levelType,
                      unit_number: row[0],
                      unit_tab_name: row[1],
                      ...(hasUnitVowel
                        ? { unit_vowel: row[base] }
                        : { unit_sentence: row[2] }),
                      ...(hasUnitKorean
                        ? { unit_korean: row[3] }
                        : hasUnitVowel
                        ? { unit_speak: row[base + 1] }
                        : {}),
                      unit_problem: hasUnitVowel
                        ? row[base + 2]
                        : row[base + 1],
                      unit_correct_word: hasUnitVowel
                        ? row[base + 3]
                        : row[base + 2],
                      unit_incorrect_word: hasUnitVowel
                        ? row[base + 4]
                        : row[base + 3],
                      unit_sort: hasUnitVowel ? row[base + 5] : row[base + 4],
                    };
                  },
                  files: (row) => {
                    const hasUnitSort =
                      row.length > 8 || typeof row[2] === "number";
                    const base = hasUnitSort ? 3 : 2;

                    return {
                      step_number: stepType,
                      level_number: levelType,
                      unit_number: row[0],
                      unit_tab_name: row[1],
                      ...(hasUnitSort ? { unit_sort: row[2] } : {}),
                      path: row[base],
                      type: row[base + 1],
                      sort: row[base + 2],
                      originalname: row[base + 3],
                      mimetype: row[base + 4],
                      size: row[base + 5],
                    };
                  },
                },
                math: {
                  units: (row) => ({
                    step_number: stepType,
                    level_number: levelType,
                    unit_number: row[0],
                    unit_title: row[1],
                    unit_point: row[2],
                    unit_main_img: row[3],
                  }),
                  details: (row) => {
                    const hasUnitSpeak = header[2] === "unit_speak";

                    const base = hasUnitSpeak ? 2 : 1;

                    return {
                      step_number: stepType,
                      level_number: levelType,
                      unit_number: row[0],
                      unit_tab_name: row[1],
                      ...(hasUnitSpeak ? { unit_speak: row[base] } : {}),
                      unit_problem: row[base + 1],
                      unit_correct_word: row[base + 2],
                      unit_incorrect_word: row[base + 3],
                      unit_sort: row[base + 4],
                    };
                  },
                  files: (row) => {
                    const hasUnitSort =
                      row.length > 8 || typeof row[2] === "number";
                    const base = hasUnitSort ? 3 : 2;
                    return {
                      step_number: stepType,
                      level_number: levelType,
                      unit_number: row[0],
                      unit_tab_name: row[1],
                      ...(hasUnitSort ? { unit_sort: row[2] } : {}),
                      path: row[base],
                      type: row[base + 1],
                      sort: row[base + 2],
                      originalname: row[base + 3],
                      mimetype: row[base + 4],
                      size: row[base + 5],
                    };
                  },
                },
              };

              const processedData = jsonData
                .slice(1)
                .map((row) => {
                  const parser = parseRules[subjectType]?.[subDetailsType];
                  if (!parser) {
                    console.warn(
                      `❗ 매핑 규칙이 정의되지 않음: ${subjectType}/${subDetailsType}`
                    );
                    return null;
                  }
                  return parser(row);
                })
                .filter(Boolean);

              const { unitsData, unitDetailsData, unitFiles, errorState } =
                processData(processedData);

              console.log("!! DATA: ", unitFiles);

              if (errorState.includes(true)) {
                alert("현재 데이터 중 잘못된 값이 있습니다.");
                setIsLoading(false);
              } else {
                const formData = new FormData();
                formData.append("unitsData", JSON.stringify(unitsData || []));
                formData.append(
                  "unitDetailsData",
                  JSON.stringify(unitDetailsData || [])
                );
                formData.append("unitFiles", JSON.stringify(unitFiles || []));

                try {
                  await filesUploads(formData);
                  console.log("!! 업로드 완료");
                } catch (error) {
                  setIsLoading(false);
                }
              }
            }
          };
          reader.readAsArrayBuffer(file);
        } else {
          setIsLoading(false);
          return alert("파일을 업로드하세요.");
        }
      } catch (e) {
        setIsLoading(false);
      }
    };

    const processData = (data: any[]) => {
      const units: { [key: string]: any } = {};
      const unitDetails: { [key: string]: any } = {};
      const unitFiles: any[] = [];
      let errorState = [false];

      data.forEach((row) => {
        const {
          step_number,
          level_number,
          unit_number,
          unit_title,
          level_id,
          unit_point,
          unit_main_img,
          unit_tab_name,
          unit_vowel,
          unit_speak,
          unit_sentence,
          unit_problem,
          unit_correct_word,
          unit_incorrect_word,
          unit_sort,
          path,
          type,
          sort,
          originalname,
          mimetype,
          size,
        } = row;

        if (unit_title) {
          units[unit_number] = {
            step_number,
            level_number,
            unit_number,
            unit_title,
            level_id,
            unit_point,
            unit_main_img,
          };
        }

        if (subjectType === "math") {
          if (
            unit_tab_name &&
            (unit_correct_word || unit_speak || unit_problem)
          ) {
            const key = `${unit_number}_${unit_sort}`;

            unitDetails[key] = {
              step_number,
              level_number,
              unit_number,
              unit_tab_name,
              unit_speak,
              unit_sentence,
              unit_problem,
              unit_correct_word,
              unit_incorrect_word,
              unit_sort,
            };
          }
        } else {
          if (unit_tab_name && (unit_sentence || unit_correct_word)) {
            const key = `${unit_number}_${unit_sort}`;

            unitDetails[key] = {
              step_number,
              level_number,
              unit_number,
              unit_tab_name,
              unit_vowel,
              unit_speak,
              unit_sentence,
              unit_problem,
              unit_correct_word,
              unit_incorrect_word,
              unit_sort,
            };
          }
        }

        if (path) {
          unitFiles.push({
            step_number,
            level_number,
            unit_number,
            unit_tab_name,
            unit_sort,
            path,
            type,
            sort,
            originalname,
            mimetype,
            size,
          });
        }
      });

      const unitsData = Object.values(units);
      const unitDetailsData = Object.values(unitDetails);
      return { unitsData, unitDetailsData, unitFiles, errorState };
    };

    return (
      <S.UploadContainer>
        <S.FileInput type="file" onChange={handleFileChange} />
        <S.Button onClick={handleUpload}>Upload</S.Button>
      </S.UploadContainer>
    );
  };

  return (
    <S.PageContainer>
      <S.Title>
        {stepType === 1
          ? "STEP01 "
          : stepType === 2
          ? "STEP02 "
          : stepType === 3
          ? "STEP03 "
          : "STEP04 "}
        {levelType === 1
          ? "LEVEL01 "
          : levelType === 2
          ? "LEVEL02 "
          : levelType === 3
          ? "LEVEL03 "
          : "LEVEL04 "}
        {subjectType} 데이터 엑셀로 넣기
      </S.Title>
      <S.PageBox>
        <S.SelectContainer>
          <S.Button
            bgColor="hangeulTheme"
            bgHover="hangeulThemeHover"
            onClick={() => setSubjectType("korean")}
          >
            한글
          </S.Button>
          <S.Button
            bgColor="englishTheme"
            bgHover="englishThemeHover"
            onClick={() => setSubjectType("english")}
          >
            영어
          </S.Button>
          <S.Button
            bgColor="mathTheme"
            bgHover="mathThemeHover"
            onClick={() => setSubjectType("math")}
          >
            수학
          </S.Button>
        </S.SelectContainer>
        <S.MainUploadsContainter>
          <S.UploadsMainLayer>
            <S.TextBox>
              <S.WarningText>
                * 데이터를 처음 넣을 때는 유닛부터 넣어야 됨
              </S.WarningText>
              <S.SubTitle>
                {stepType === 1
                  ? "STEP01 "
                  : stepType === 2
                  ? "STEP02 "
                  : stepType === 3
                  ? "STEP03 "
                  : "STEP04 "}
                {levelType === 1
                  ? "LEVEL01 "
                  : levelType === 2
                  ? "LEVEL02 "
                  : levelType === 3
                  ? "LEVEL03 "
                  : "LEVEL04 "}
                {subjectType}의 {subDetailsType} 데이터 엑셀로 넣기
              </S.SubTitle>
            </S.TextBox>
          </S.UploadsMainLayer>
          {/** STEP */}
          <S.SelectContainer>
            <S.Button onClick={() => setStepType(1)}>STEP01</S.Button>
            <S.Button onClick={() => setStepType(2)}>STEP02</S.Button>
            <S.Button onClick={() => setStepType(3)}>STEP03</S.Button>
            <S.Button onClick={() => setStepType(4)}>STEP04</S.Button>
          </S.SelectContainer>
          {/** LEVEL */}
          <S.SelectContainer>
            <S.Button onClick={() => setLevelType(1)}>LEVEL01</S.Button>
            <S.Button onClick={() => setLevelType(2)}>LEVEL02</S.Button>
            <S.Button onClick={() => setLevelType(3)}>LEVEL03</S.Button>
            <S.Button onClick={() => setLevelType(4)}>LEVEL04</S.Button>
          </S.SelectContainer>
          {/** UNIT & DETAILS & FILES */}
          <S.SelectContainer>
            <S.Button onClick={() => OpenUploadsModal("units")}>유닛</S.Button>
            <S.Button onClick={() => OpenUploadsModal("details")}>
              세부 컨텐츠
            </S.Button>
            <S.Button onClick={() => OpenUploadsModal("files")}>파일</S.Button>
          </S.SelectContainer>
        </S.MainUploadsContainter>
      </S.PageBox>
      {isModalOpen && (
        <Modal
          size="lg"
          title={`${subDetailsType} 데이터 넣기`}
          onClose={() => setIsModalOpen(false)}
        >
          <S.InputBox>
            <InputText
              label="카테고리"
              placeholder={subjectType}
              disabled={true}
            />
            <InputText
              label="데이터"
              placeholder={subDetailsType}
              disabled={true}
            />
            <FileUpload project="data-uploads" />
          </S.InputBox>
        </Modal>
      )}
      {isLoading && (
        <Modal>
          <p>로딩 중입니다...</p>
        </Modal>
      )}
    </S.PageContainer>
  );
};
